<script lang="ts" setup>
import { computed, reactive, ref, Ref, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';

import { useUiStore } from '@/stores';
import { can } from '@/composables/useAuth';
import { goBack } from '@/composables/useNavigation';

import {
    containsLowercase,
    containsNumber,
    containsSpecial,
    containsUppercase,
    email,
    maxLength,
    minLength,
    required,
    sameAs,
} from '@/utils/validators';

import { toast, toastServiceError } from '@/services/core/notification';
import { UserService, SelectOptionsService } from '@/services';

import {
    DefaultServiceResult,
    FormAction,
    Permission,
    SelectOptionsServiceGetLevelsResult,
    SelectOptionsServiceGetNumbersResult,
    SelectOptionsServiceGetRolesResult,
    UserDeviceType,
    UserFormState,
    UserModel,
    UserServiceCreateResult,
} from '@/types';

import { UserFormDevice } from '@/views/pages/user/components';
import { DesktopComputerIcon, DeviceMobileIcon } from '@heroicons/vue/solid';
import { InformationCircleIcon } from '@heroicons/vue/solid';
import SVGDeviceExternal from '@/components/svg/SVGDeviceExternal.vue';
import SVGChromeLogo from '@/components/svg/SVGChromeLogo.vue';

const route = useRoute();
const { t } = useI18n();
const uiStore = useUiStore();

const props = defineProps({
    action: { type: String, default: 'view' },
});

const formState = reactive<UserFormState>({
    name: null,
    email: null,
    roles: [],
    levels: [],
    standardLevelId: null,
    isActive: true,
    extensionNumber: null,
    numberId: null,
    useSmartCallerId: false,
    useAutoAttendant: false,
    devices: {
        mobile: false,
        desktop: false,
        web: false,
        external: false,
    },
});

const isLoading = ref(false);
const levelsOptions: Ref<SelectOptionsServiceGetLevelsResult[]> = ref([]);
const numbersOptions: Ref<SelectOptionsServiceGetNumbersResult[]> = ref([]);
const rolesOptions: Ref<SelectOptionsServiceGetRolesResult[]> = ref([]);

const externalConfig = ref();

const noDevice = computed(() => Object.values(formState.devices).every((v) => !v));
const selectedLevels = computed(() =>
    levelsOptions.value.filter((lvl) => formState.levels.includes(lvl.id)),
);

const disableAutoAttendant = computed(
    () => !(formState.devices.mobile || formState.devices.desktop),
);

const autoAttendantTooltip = computed(() => {
    return `<span class='text-base'>${t('user.tooltips.useAutoAttendant')}</span>`;
});

const rules = computed(() => {
    const r = {
        name: {
            required,
            maxLength: maxLength(500),
        },
        email: {
            required,
            email,
            maxLength: maxLength(250),
        },
        roles: { required },
        levels: { required },
        standardLevelId: { required },
        isActive: { required },
        ...(props.action === FormAction.CREATE && {
            password: {
                required,
                minLength: minLength(8),
                maxLength: maxLength(250),
                containsUppercase,
                containsLowercase,
                containsNumber,
                containsSpecial,
            },
            confirmPassword: {
                required,
                sameAsPassword: sameAs(formState.password, t('user.labels.password')),
            },
        }),
    };

    return r;
});

const v$ = useVuelidate(rules, formState);

const hasSmartCallerId = computed(() =>
    formState.standardLevelId ? numbersOptions.value.some((n) => n.isSmartCallerId) : false,
);

const setLoading = (loading: boolean) => {
    isLoading.value = loading;
    uiStore.setIsLoading(loading);
};

const fetchLevels = async () => {
    try {
        const response = await SelectOptionsService.getLevels();
        levelsOptions.value = response.data ?? [];
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchRoles = async () => {
    try {
        const response = await SelectOptionsService.getRoles();
        rolesOptions.value = response.data ?? [];
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchUser = async (id: number) => {
    try {
        const response = await UserService.get<UserModel>(id);
        const {
            name,
            email,
            levels,
            roles,
            isActive,
            standardLevelId,
            numbers,
            extension,
            devices,
            useSmartCallerId,
            useAutoAttendant,
        } = response.data;

        Object.assign(formState, {
            id,
            name,
            email,
            roles: roles?.map((l) => l.id) || [],
            levels: levels?.map((l) => l.id) || [],
            standardLevelId: standardLevelId ?? null,
            isActive,
            extensionNumber: extension?.number || null,
            numberId: numbers?.length ? numbers[0].id : null,
            useSmartCallerId: useSmartCallerId || false,
            useAutoAttendant: useAutoAttendant || false,
            devices: {
                mobile: devices?.some((d) => d.deviceType === UserDeviceType.MOBILE),
                desktop: devices?.some((d) => d.deviceType === UserDeviceType.DESKTOP),
                web: devices?.some((d) => d.deviceType === UserDeviceType.WEB),
                external: devices?.some((d) => d.deviceType === UserDeviceType.EXTERNAL),
            },
        });

        await fetchNumbers();

        const deviceExternal = devices?.find((d) => d.deviceType === UserDeviceType.EXTERNAL);
        if (deviceExternal) {
            externalConfig.value = {
                hostname: deviceExternal.hostname,
                sipUser: deviceExternal.sipUser,
                sipPassword: deviceExternal.sipPassword,
            };
        }
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchNumbers = async () => {
    try {
        if (!formState.standardLevelId) {
            numbersOptions.value = [];
            return;
        }

        const response = await SelectOptionsService.getNumbers({
            params: { level: formState.standardLevelId },
        });
        numbersOptions.value = response.data;
    } catch (error) {
        toastServiceError(error);
    }
};

const onSubmit = async () => {
    const isValid = await v$.value.$validate();
    if (!isValid) return;

    setLoading(true);
    try {
        const id = Number(route.params.id);

        if (!noDevice.value) {
            const isExtensionNumberTaken = await UserService.checkExtensionNumber({
                id: props.action === FormAction.EDIT ? id : null,
                levelId: formState.standardLevelId,
                number: formState.extensionNumber,
            });

            if (isExtensionNumberTaken) {
                throw new Error(t('user.error.extensionUsed'));
            }
        }

        const response =
            props.action === FormAction.EDIT
                ? await UserService.update<DefaultServiceResult, UserFormState>(id, formState)
                : await UserService.create<UserServiceCreateResult, UserFormState>(formState);

        toast.success(response.data.message);
        v$.value.$reset();
        goBack({ name: 'ViewUserById', params: { id } });
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

watch(
    () => formState.levels,
    () => {
        if (formState.standardLevelId && !formState.levels.includes(formState.standardLevelId)) {
            formState.standardLevelId = null;
        }
    },
);

watch(
    () => formState.standardLevelId,
    (newValue) => {
        if (!isLoading.value) {
            formState.numberId = null;
            formState.useSmartCallerId = false;
            if (newValue) fetchNumbers();
        }
    },
);

watch(
    () => formState.useSmartCallerId,
    (newValue) => {
        if (newValue && formState.numberId) formState.numberId = null;
    },
);

watch(
    () => [formState.devices.mobile, formState.devices.desktop],
    () => {
        if (disableAutoAttendant.value && formState.useAutoAttendant) {
            formState.useAutoAttendant = false;
        }
    },
);

onMounted(async () => {
    setLoading(true);
    try {
        const fetchs = [];

        if (can(Permission.ASSIGN_LEVEL_TO_USER)) fetchs.push(fetchLevels());
        if (can(Permission.ASSIGN_ROLE_TO_USER)) fetchs.push(fetchRoles());
        if (props.action === FormAction.EDIT) fetchs.push(fetchUser(Number(route.params.id)));

        await Promise.all(fetchs);
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
});
</script>

<template>
    <form class="space-y-4 p-4 sm:p-6 lg:p-8" autocomplete="off" @submit.prevent="onSubmit">
        <UiPageHeader>
            <template #info>
                <h1 class="text-xl font-semibold text-gray-800">
                    {{ $t('user.' + props.action + '.title') }}
                </h1>
            </template>
            <template #actions>
                <div class="flex items-center space-x-4">
                    <UiBackButton :fallbackTo="{ name: 'ListUser' }" />
                    <UiButton
                        v-if="
                            (props.action == FormAction.CREATE && $can(Permission.CREATE_USER)) ||
                            (props.action == FormAction.EDIT && $can(Permission.EDIT_USER))
                        "
                        type="submit"
                        variant="primary"
                        text-variant="white"
                    >
                        {{ $t('core.actions.Save') }}
                    </UiButton>
                </div>
            </template>
        </UiPageHeader>
        <UiPanel>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                <UiTextInput
                    v-model="formState.name"
                    name="name"
                    type="text"
                    :label="$t('user.labels.name')"
                    :errors="v$.name.$errors"
                    :required="v$.name.required !== undefined"
                    @blur="v$.name.$touch"
                />
                <UiTextInput
                    v-model="formState.email"
                    name="email"
                    type="text"
                    :label="$t('user.labels.email')"
                    autocomplete="new-email"
                    :errors="v$.email.$errors"
                    :required="v$.email.required !== undefined"
                    @blur="v$.email.$touch"
                />
                <div v-if="$can(Permission.ASSIGN_ROLE_TO_USER)">
                    <label for="roles" class="mb-2 block text-sm font-medium text-gray-800">
                        {{ $t('user.labels.roles') }}
                        <span class="text-xs">*</span>
                    </label>
                    <UiMultiselect
                        name="roles"
                        id="roles"
                        v-model="formState.roles"
                        :options="rolesOptions"
                        primitive
                        multiple
                        can-clear
                        :errors="v$.roles?.$errors"
                        label="name"
                        value-prop="id"
                    />
                </div>
                <div v-else class="hidden md:block"></div>
                <UiTextInput
                    v-if="props.action == FormAction.CREATE"
                    v-model="formState.password"
                    name="password"
                    type="password"
                    :label="$t('user.labels.password')"
                    autocomplete="new-password"
                    :errors="v$.password?.$errors"
                    :required="v$.password?.required !== undefined"
                    @blur="v$.password?.$touch"
                />
                <UiTextInput
                    v-if="props.action == FormAction.CREATE"
                    v-model="formState.confirmPassword"
                    name="confirmPassword"
                    type="password"
                    :label="$t('user.labels.confirmPassword')"
                    :errors="v$.confirmPassword?.$errors"
                    :required="v$.confirmPassword?.required !== undefined"
                    @blur="v$.confirmPassword?.$touch"
                />
                <div v-if="props.action == FormAction.CREATE" class="hidden md:block"></div>
                <div v-if="$can(Permission.ASSIGN_LEVEL_TO_USER)">
                    <label for="levels" class="mb-2 block text-sm font-medium text-gray-800">
                        {{ $t('user.labels.levels') }}
                        <span class="text-xs">*</span>
                    </label>
                    <UiMultiselect
                        name="levels"
                        id="levels"
                        v-model="formState.levels"
                        :options="levelsOptions"
                        primitive
                        multiple
                        can-clear
                        :errors="v$.levels?.$errors"
                        label="name"
                        value-prop="id"
                    />
                </div>
                <div>
                    <label for="levels" class="mb-2 block text-sm font-medium text-gray-800">
                        {{ $t('user.labels.standardLevel') }}
                        <span class="text-xs">*</span>
                    </label>
                    <UiMultiselect
                        name="standardLevel"
                        id="standardLevel"
                        v-model="formState.standardLevelId"
                        :options="selectedLevels"
                        :required="v$.standardLevelId.required !== undefined"
                        :errors="v$.standardLevelId?.$errors"
                        primitive
                        label="name"
                        value-prop="id"
                    />
                </div>
                <div class="flex flex-col justify-end pb-1.5">
                    <UiCheckboxInput
                        v-model="formState.isActive"
                        name="isActive"
                        :label="$t('user.labels.isActive')"
                        class="h-6"
                    />
                </div>
            </div>
        </UiPanel>

        <UiPanel>
            <h3 class="mb-4 text-base font-semibold text-gray-800">
                {{ $t('user.labels.devices.title') }}
            </h3>
            <div class="relative grid grid-cols-2 gap-4 lg:grid-cols-4 xl:grid-cols-6">
                <UserFormDevice
                    :label="$t('user.labels.devices.mobile')"
                    v-model="formState.devices.mobile"
                >
                    <template #icon>
                        <DeviceMobileIcon class="mt-6 h-20 w-20" />
                    </template>
                </UserFormDevice>
                <UserFormDevice
                    :label="$t('user.labels.devices.desktop')"
                    v-model="formState.devices.desktop"
                >
                    <template #icon>
                        <DesktopComputerIcon class="mt-6 h-20 w-20" />
                    </template>
                </UserFormDevice>
                <UserFormDevice
                    :label="$t('user.labels.devices.web')"
                    v-model="formState.devices.web"
                >
                    <template #icon>
                        <SVGChromeLogo class="mt-6 h-20 w-20" />
                    </template>
                </UserFormDevice>
                <UserFormDevice
                    :label="$t('user.labels.devices.external')"
                    v-model="formState.devices.external"
                >
                    <template #icon>
                        <SVGDeviceExternal class="mt-6 h-20 w-20" />
                    </template>
                    <template #extra-info v-if="externalConfig">
                        <div
                            class="absolute -right-[215px] bottom-0 mt-6 flex flex-col justify-between rounded border-b-4 border-r-4 border-cyan-600 bg-gray-50 px-4 py-3 text-sm text-gray-600 shadow"
                        >
                            <h3 class="mb-1 text-base font-semibold">Configurações do PABX</h3>
                            <p>
                                <span class="font-semibold">Hostname:</span>
                                {{ externalConfig.hostname }}
                            </p>
                            <p>
                                <span class="font-semibold">Username:</span>
                                {{ externalConfig.sipUser }}
                            </p>
                            <p>
                                <span class="font-semibold">Password:</span>
                                {{ externalConfig.sipPassword }}
                            </p>
                        </div>
                    </template>
                </UserFormDevice>
            </div>
            <div v-if="!noDevice" class="relative mt-5 grid grid-cols-2 gap-4 md:grid-cols-12">
                <UiTextInput
                    v-model="formState.extensionNumber"
                    name="extensionNumber"
                    type="number"
                    :label="$t('user.labels.extensionNumber')"
                    class="col-span-3"
                />
                <div class="col-span-3">
                    <label for="level" class="mb-2 block text-sm font-medium text-gray-800">
                        {{ $t('user.labels.number') }}
                    </label>
                    <UiMultiselect
                        name="number"
                        v-model="formState.numberId"
                        :placeholder="$t('core.random')"
                        :options="numbersOptions"
                        primitive
                        can-clear
                        :disabled="noDevice || formState.useSmartCallerId"
                        label="number"
                        value-prop="id"
                    />
                    <div v-if="hasSmartCallerId" class="mt-2">
                        <UiCheckboxInput
                            v-model="formState.useSmartCallerId"
                            :label="$t('user.labels.useSmartCallerId')"
                            name="useSmartCallerId"
                        />
                    </div>
                    <div class="mt-4 flex items-center gap-2">
                        <UiCheckboxInput
                            v-model="formState.useAutoAttendant"
                            :label="$t('user.labels.useAutoAttendant')"
                            name="useAutoAttendant"
                            :disabled="disableAutoAttendant"
                        />
                        <InformationCircleIcon
                            class="h-5 w-5 text-gray-400"
                            aria-hidden="true"
                            v-tippy="{ content: autoAttendantTooltip, allowHTML: true }"
                        />
                    </div>
                </div>
            </div>
        </UiPanel>
        <UiCheckDirtyBeforeRouteLeave :dirty="v$.$anyDirty" />
    </form>
</template>
